import { ChangeEvent, memo, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { Content } from 'src/contexts/ContentContext';
import { Switch as SwitchV1 } from 'src/core/interim/Switch';
import { vars } from 'src/core/themes';
import { Stack, Switch } from 'src/core/ui';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { useListingAutoPricingSettings } from 'src/modals/ListingDetails/components/useListingAutoPricingSettings';
import { ProcessingIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';

import {
  ListingPricingInput,
  ListingPricingSettingsCellProps,
} from '../ListingPriceForm.types';
import { useCellDisabled, useIsSubmittingField } from '../useCellState';

const CompListingExcludeFanInventoryCellRaw: React.FC<
  ListingPricingSettingsCellProps
> = ({ listing, editable, event, isV2Variant = false }) => {
  const { watch, setValue } = useFormContext<ListingPricingInput>();

  const { compListingExcludeFanInventory, canAutoPrice, onCompListingChange } =
    useListingAutoPricingSettings(listing);

  const rowIndex = watch('rowIndex');
  const isDisabled = useCellDisabled(
    listing,
    canAutoPrice,
    editable,
    event.virtId
  );
  const isSubmittingField = useIsSubmittingField(
    'compListingExcludeFanInventory'
  );

  const onToggleExcludeFanInventoryV1 = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const isChecked = e.target.checked;
      if (isChecked === compListingExcludeFanInventory) {
        return;
      }
      onCompListingChange({
        compListingExcludeFanInventoryNew: isChecked,
      });
      setValue('isSubmittingPricingSettings', true);
      setValue('isSubmittingRowIndex', rowIndex);
      setValue('submittingField', 'compListingExcludeFanInventory');
    },
    [compListingExcludeFanInventory, onCompListingChange, rowIndex, setValue]
  );

  const onToggleExcludeFanInventoryV2 = useCallback(
    (checked: boolean) => {
      if (checked === compListingExcludeFanInventory) {
        return;
      }
      onCompListingChange({
        compListingExcludeFanInventoryNew: checked,
      });
      setValue('isSubmittingPricingSettings', true);
      setValue('isSubmittingRowIndex', rowIndex);
      setValue('submittingField', 'compListingExcludeFanInventory');
    },
    [compListingExcludeFanInventory, onCompListingChange, rowIndex, setValue]
  );

  const content = (
      <Stack direction="row" gap="s" alignItems="center">
        {isSubmittingField ? (
          <div className="operation-button">
            <RotatingWrapper>
              <ProcessingIcon size={vars.iconSize.m} />
            </RotatingWrapper>
          </div>
        ) : isV2Variant ? (
          <Switch
            disabled={isDisabled}
            checked={Boolean(compListingExcludeFanInventory)}
            onCheckedChange={onToggleExcludeFanInventoryV2}
          />
        ) : (
          <>
            <SwitchV1
              disabled={isDisabled}
              checked={Boolean(compListingExcludeFanInventory)}
              onClick={(e: ChangeEvent<HTMLElement>) => e.stopPropagation()}
              onChange={onToggleExcludeFanInventoryV1}
            />
            <Content
              id={compListingExcludeFanInventory ? ContentId.On : ContentId.Off}
            />
          </>
        )}
      </Stack>
  );

  return isV2Variant ? (
    content
  ) : (
    <TableCellDiv align="right" showTooltip={false}>
      {content}
    </TableCellDiv>
  );
};

export const CompListingExcludeFanInventoryCell = memo(
  CompListingExcludeFanInventoryCellRaw
);
